import { MsalProvider } from "@azure/msal-react";
import { broadcastResponseToMainFrame } from "@azure/msal-browser/redirect-bridge";
import { ErrorInfo, CSSProperties } from "react";
import { createRoot } from "react-dom/client";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import App from "./App";

const fluentProviderStyle: CSSProperties = { height: "100%" };
import { handleRedirectPromise, initializeMsal, msalInstance } from "./Common/Auth";
import { ErrorBoundary } from "./Components/ErrorBoundary";
import { ErrorModule } from "./Components/ErrorModule";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { appInsights, reactPlugin } from "./AppInsights";

appInsights.trackPageView();

// When MSAL's acquireTokenSilent renews a token via a hidden iframe, the iframe loads
// this app's URL. If the app fully renders inside that iframe it will recursively call
// acquireTokenSilent, causing "block_iframe_reload" and "timed_out" errors.
// Bail out early: broadcast the auth response back to the main window via the redirect
// bridge (required since MSAL v4+), then let MSAL handle the redirect response.
if (window.parent !== window) {
    broadcastResponseToMainFrame().catch(console.error);
    initializeMsal().then(() => handleRedirectPromise());
} else {
    const renderError = (error: Error, errorInfo: ErrorInfo) => <ErrorModule error={error} errorInfo={errorInfo} />;

    initializeMsal().then(() => {
        const container = document.getElementById("root");
        if (!container) {
            return;
        }
        const root = createRoot(container);
        root.render(
            // Note: React.StrictMode is intentionally omitted.
            // FluentUI v8 DetailsList/Selection uses class component lifecycle methods
            // that break under StrictMode's double-mount cycle in Vite dev mode.
            // See: https://github.com/microsoft/fluentui/issues/32808
            <ErrorBoundary renderError={renderError}>
                <AppInsightsContext.Provider value={reactPlugin}>
                    <MsalProvider instance={msalInstance}>
                        <FluentProvider theme={webLightTheme} style={fluentProviderStyle}>
                            <App />
                        </FluentProvider>
                    </MsalProvider>
                </AppInsightsContext.Provider>
            </ErrorBoundary>
        );
    });
}
